import { Autocomplete, Box, Button, TextField } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBulkUpdateDevices } from '~/api/devices';
import { Dialog, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib/styles';
import type { DeviceBulkSelectionInput } from '~/generated/graphql';
import type { DeviceList__DeviceGroup as DeviceGroup } from '../../queries/list.generated';

export interface BulkDeviceGroupDialogProps {
  deviceGroups: ReadonlyArray<DeviceGroup>;
  onClose: () => void;
  onComplete: () => void;
  open: boolean;
  selected: DeviceBulkSelectionInput;
}

export const BulkDeviceGroupDialog = ({
  deviceGroups,
  onClose,
  onComplete,
  open,
  selected,
}: BulkDeviceGroupDialogProps) => {
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const { t } = useTranslation();

  const [updateDeviceGroup, { loading }] = useBulkUpdateDevices();

  const options = useMemo(
    () => [
      { label: t('common.labels.none'), value: null },
      ...deviceGroups.map((x) => ({ label: x.name, value: x.id })),
    ],
    [deviceGroups, t],
  );

  const handleClose = useCallback(() => {
    setSelectedGroupId(() => null);
    onClose();
  }, [onClose]);

  return (
    <Dialog maxWidth="xs" fullWidth onClose={handleClose} open={open}>
      <DialogTitle onClose={handleClose}>
        {t('pages.devices.bulk_actions.add_to_device_group')}
      </DialogTitle>

      <DialogContent>
        <Box display="flex" alignItems="center" justifyContent="center" padding={2}>
          <Autocomplete
            fullWidth
            getOptionLabel={(option) => option.label}
            noOptionsText={t('common.labels.no_options')}
            onChange={(_event, option) => setSelectedGroupId(option?.value ?? null)}
            options={options}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('pages.devices.bulk_actions.device_group.title')}
                variant="outlined"
              />
            )}
            value={options.find((x) => x.value === selectedGroupId) || null}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={handleClose}>
          {t('common.dialogs.cancel')}
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={loading}
          onClick={async () => {
            await updateDeviceGroup({
              variables: { input: { patch: { deviceGroupId: selectedGroupId }, selected } },
            });
            handleClose();
            onComplete();
          }}
        >
          {t('common.dialogs.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
